import React, { useEffect, useState } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	Typography,
} from "@material-ui/core";
import { DeleteOutline } from "@material-ui/icons";

import api from "../../services/api";
import formatConnectionNumber from "../../helpers/formatConnectionNumber";

const useStyles = makeStyles(theme => {
	const red = theme.palette.type === "dark" ? "#ef5350" : "#c62828";
	return {
		head: { display: "flex", gap: theme.spacing(1.75), padding: theme.spacing(2.75, 3, 1) },
		icon: {
			width: 40,
			height: 40,
			borderRadius: 12,
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			flexShrink: 0,
			backgroundColor: alpha(red, 0.1),
			color: red,
		},
		title: { fontSize: 18, fontWeight: 600 },
		sub: { fontSize: 13, color: theme.palette.text.secondary, marginTop: 2 },
		content: { padding: theme.spacing(1, 3, 2.25), fontSize: 14, lineHeight: 1.5 },
		list: {
			margin: 0,
			paddingLeft: 20,
			display: "flex",
			flexDirection: "column",
			gap: 6,
			color: theme.palette.text.secondary,
			"& b": { color: theme.palette.text.primary },
		},
		hint: {
			marginTop: theme.spacing(1.75),
			padding: theme.spacing(1.5, 1.75),
			borderRadius: 12,
			backgroundColor: theme.palette.action.hover,
			fontSize: 13,
		},
		linkBtn: {
			padding: 0,
			minWidth: 0,
			textTransform: "none",
			fontWeight: 600,
			fontSize: 13,
			verticalAlign: "baseline",
		},
		actions: {
			padding: theme.spacing(1.75, 3),
			borderTop: `1px solid ${theme.palette.divider}`,
			gap: theme.spacing(1),
		},
		btn: { textTransform: "none", fontWeight: 600, borderRadius: 8, boxShadow: "none" },
		danger: {
			backgroundColor: red,
			color: "#fff",
			"&:hover": { backgroundColor: alpha(red, 0.85), boxShadow: "none" },
		},
	};
});

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Confirmação de exclusão que mostra o que deixa de funcionar antes do
// usuário decidir, e oferece "Desconectar" como alternativa sem perda.
const DeleteConnectionModal = ({ whatsApp, onClose, onConfirm, onDisconnectInstead }) => {
	const classes = useStyles();
	const [impact, setImpact] = useState(null);
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const open = !!whatsApp;

	useEffect(() => {
		if (!whatsApp) {
			setImpact(null);
			setSubmitting(false);
			return;
		}
		let active = true;
		setLoading(true);
		api
			.get(`/whatsapp/${whatsApp.id}/delete-impact`)
			.then(({ data }) => active && setImpact(data))
			// Sem o resumo a confirmação continua funcionando, só sem os números.
			.catch(() => active && setImpact(null))
			.finally(() => active && setLoading(false));
		return () => {
			active = false;
		};
	}, [whatsApp]);

	const handleConfirm = async () => {
		setSubmitting(true);
		await onConfirm();
	};

	const number = formatConnectionNumber(whatsApp?.number);
	const isConnected = whatsApp?.status === "CONNECTED";

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
			<div className={classes.head}>
				<span className={classes.icon}>
					<DeleteOutline />
				</span>
				<div>
					<Typography className={classes.title}>Excluir a conexão {whatsApp?.name}?</Typography>
					{number && <Typography className={classes.sub}>{number}</Typography>}
				</div>
			</div>
			<DialogContent className={classes.content}>
				<p style={{ margin: "0 0 10px" }}>O que acontece:</p>
				<ul className={classes.list}>
					<li>O número é desligado do Message Express.</li>
					<li>
						{loading ? (
							<CircularProgress size={12} style={{ marginRight: 6 }} />
						) : impact ? (
							<b>{plural(impact.openTickets, "atendimento aberto", "atendimentos abertos")}</b>
						) : (
							<b>Os atendimentos abertos</b>
						)}{" "}
						{impact?.openTickets === 1 ? "fica" : "ficam"} sem conexão e não poderão mais ser respondidos por aqui.
					</li>
					{impact?.activeCampaigns > 0 && (
						<li>
							<b>{plural(impact.activeCampaigns, "campanha programada ou em andamento", "campanhas programadas ou em andamento")}</b>{" "}
							{impact.activeCampaigns === 1 ? "deixa de ser enviada" : "deixam de ser enviadas"}.
						</li>
					)}
					<li>O histórico de mensagens continua salvo.</li>
				</ul>
				{isConnected && onDisconnectInstead && (
					<div className={classes.hint}>
						Só quer trocar de celular ou parar por um tempo?{" "}
						<Button color="primary" className={classes.linkBtn} onClick={onDisconnectInstead}>
							Desconectar
						</Button>{" "}
						mantém tudo configurado.
					</div>
				)}
			</DialogContent>
			<DialogActions className={classes.actions}>
				<Button variant="outlined" className={classes.btn} onClick={onClose}>
					Cancelar
				</Button>
				<Button
					variant="contained"
					className={`${classes.btn} ${classes.danger}`}
					onClick={handleConfirm}
					disabled={submitting}
				>
					Excluir conexão
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DeleteConnectionModal;
